import React from 'react'
import { Card, Empty, Flex, Skeleton, Typography } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { TimeAgo } from '../../components/time-ago'
import { UPDATE_SITE } from '../../user/permission'
import { useUnanswered } from '../hooks'
import { UnansweredQuestion } from '../site'
import { AnswerForm } from './answer-form'
import { Transcript } from './site-inbox'

export type QuestionQueueProps = {
    siteId: string
    days: 7 | 30
    search?: string
    question?: string
    onQuestionChange: (question?: string) => void
}

/**
 * The questions Evaa could not answer as a queue, most asked first: answer one, and the next one opens. The chat where
 * a question was asked last shows under the answer, so it can be read without leaving the queue.
 */
export function QuestionQueue({ siteId, days, search, question, onQuestionChange }: QuestionQueueProps) {
    const { t } = useTranslation()
    const { data, isLoading, isFetching } = useUnanswered(siteId, days, search)
    const questions = data?.data ?? []
    const selected = questions.find((item) => item.question === question) ?? questions[0]
    const index = selected ? questions.indexOf(selected) : -1
    // After this one: the next, or back to the first when this was the last.
    const next = questions[index + 1] ?? (index > 0 ? questions[0] : undefined)

    if (isLoading) {
        return (
            <Card>
                <Skeleton active />
            </Card>
        )
    }

    if (!selected) {
        return (
            <Card>
                <Empty
                    image={
                        search ? (
                            Empty.PRESENTED_IMAGE_SIMPLE
                        ) : (
                            <CheckCircleOutlined className="text-5xl text-green-600" />
                        )
                    }
                    description={search ? t('No questions found') : t('No unanswered questions in this period.')}
                />
            </Card>
        )
    }

    return (
        <div className="grid gap-4 lg:h-[75vh] lg:min-h-[480px] lg:grid-cols-[320px_minmax(0,1fr)]">
            <Card
                size="small"
                styles={{ body: { padding: 0 } }}
                className={`min-h-0 overflow-y-auto ${isFetching ? 'opacity-70' : ''}`}
            >
                <Typography.Text
                    type="secondary"
                    className="block border-b border-neutral-100 px-3 py-2 text-xs"
                >
                    {t('Most asked first')}
                </Typography.Text>
                {questions.map((item) => (
                    <button
                        key={item.question}
                        type="button"
                        onClick={() => onQuestionChange(item.question)}
                        className={`block w-full cursor-pointer border-0 border-b border-neutral-100 px-3 py-2.5 text-left last:border-b-0 ${
                            item === selected ? 'bg-primary/10' : 'bg-transparent hover:bg-neutral-50'
                        }`}
                    >
                        <Typography.Text
                            ellipsis
                            className="block"
                        >
                            {item.question}
                        </Typography.Text>
                        <Typography.Text
                            type="secondary"
                            className="text-xs"
                        >
                            {t('asked {{count}} times', { count: item.count })}
                        </Typography.Text>
                    </button>
                ))}
            </Card>
            <Flex
                vertical
                gap="middle"
                className="min-h-0 min-w-0"
            >
                <AnswerCard
                    key={selected.question}
                    siteId={siteId}
                    item={selected}
                    onNext={() => onQuestionChange(next?.question)}
                />
                <div className="min-h-[320px] lg:min-h-0 lg:flex-1">
                    <Transcript
                        siteId={siteId}
                        chatId={selected.chatId}
                        answerable={false}
                    />
                </div>
            </Flex>
        </div>
    )
}

function AnswerCard({ siteId, item, onNext }: { siteId: string; item: UnansweredQuestion; onNext: () => void }) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const meta = (
        <Typography.Text
            type="secondary"
            className="text-xs"
        >
            {t('asked {{count}} times', { count: item.count })} · {t('last')} <TimeAgo date={item.lastAskedAt} />
        </Typography.Text>
    )

    return (
        <Card
            size="small"
            className="shrink-0"
        >
            {can(UPDATE_SITE) ? (
                <AnswerForm
                    siteId={siteId}
                    question={item.question}
                    meta={meta}
                    submitLabel={t('Save and next')}
                    cancelLabel={t('Skip')}
                    onSaved={onNext}
                    onCancel={onNext}
                />
            ) : (
                <Flex
                    vertical
                    gap={2}
                >
                    <Typography.Text className="text-base font-medium">“{item.question}”</Typography.Text>
                    {meta}
                    <Typography.Text
                        type="secondary"
                        className="mt-2"
                    >
                        {t('Only people who can change the website can add answers.')}
                    </Typography.Text>
                </Flex>
            )}
        </Card>
    )
}
